import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import channelService from "../../../services/ChannelService";
import httpService from "../../../services/HttpService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { storage } from "../../../storageconstants";
import { debugError, debugLog, syncCheckoutOverview } from "../../../utils";
import { claimAutoFinalize } from "./autoFinalize";

export default class FastTrackPaymentController extends Controller {
  static targets = ["submitButton", "processing", "retry"];

  static values = {
    code: String,
    permitid: Number,
    tokensymbol: String,
    challenge: String,
    typeddata: String,
    address: String,
    session: String,
    keycredential: Boolean,
    networkName: String,
    amountDisplay: String,
    channelid: String,
    /** Session checkout_path — wallet_management when WM checkout reached Pay Now */
    checkoutPath: String,
    /** Human-scale pay total for Statsig (USD total; stablecoin ≈ 1:1 for checkout display) */
    wmAmountCrypto: String,
    /** Onramp checkout whose card payment already funded the wallet: start the payment on load (CS-3534) */
    autoFinalize: Boolean,
  }

  async connect() {
    this.hideLoadingScreen();

    this.attestation = null;

    this.domain = JSON.parse(sessionStorage.getItem(storage.DOMAIN));
    this.challenge = JSON.parse(this.challengeValue);
    this.typedData = JSON.parse(this.typeddataValue);
    this.keyCredential = this.keycredentialValue;
    syncCheckoutOverview({
      networkName:   this.networkNameValue,
      amountDisplay: this.amountDisplayValue,
    });

    // Pre-subscribe to the Pusher channel at controller-mount time so the
    // first progress event published after /payment/ returns is not missed.
    // Same channel name the backend will publish to (account.GetChannel());
    // ChannelService deduplicates repeat subscribes by name.
    if (this.channelidValue) {
      channelService.connect(this.channelidValue);
    }

    if (claimAutoFinalize(this.autoFinalizeValue, sessionStorage, this.sessionValue)) {
      this.submitButtonTarget.disabled = true;
      try {
        await this.initiateFastTrackPayment();
      } catch (error) {
        debugError("Automatic finalize failed:", error);
        this.hideLoadingScreen();
        this.releaseButton();
      }
    }
  }

  async initiateFastTrackPayment() {
    if (
      (this.checkoutPathValue || "").trim() === "wallet_management" &&
      this.sessionValue
    ) {
      sessionStorage.setItem(storage.SESSION, this.sessionValue);
      emitCheckoutEvent("checkout_wm_confirm_pay_clicked", {
        asset: (this.tokensymbolValue || "").trim(),
        amount_crypto: (this.wmAmountCryptoValue || "").trim(),
      });
    }

    this.showLoadingScreen("Initiating payment...");
    const initiateFastTrackRequest = await fetch(
      `initiate-fast-track-payment?session=${this.sessionValue}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session": this.sessionValue,
          "X-Code": this.codeValue,
        }
      }
    );

    if (!initiateFastTrackRequest.ok) {
      debugError("Error in initiateFastTrackRequest!");
      this.hideLoadingScreen();
      this.releaseButton();
      return 
    }

    const initiateFastTrackResponse = await initiateFastTrackRequest.json();

    debugLog("initiateFastTrackResponse:", initiateFastTrackResponse);

    this.showLoadingScreen("Payment in progress...");

    if (initiateFastTrackResponse.status === "done") {
      this.handleSignChallenge();
    }
  }

  async handleSignChallenge() {
    try {
      if (!this.challenge) throw new Error("No challenge available to sign");
      if (!this.domain)    throw new Error("Domain data not available");

      if (this.keyCredential) {
        debugLog("Using keycredential for signing");

        const signResonse = await httpService.post(`/sign/`, {
          challenge: this.challenge,
          session: this.sessionValue,
        });

        if (signResonse.error) {
          debugError(signResonse.error);
          throw new Error(signResonse.error);
        }

        debugLog("KeyCredential signing successful:", signResonse.data);

        this.attestation = signResonse.data;
      } else {
        debugLog("Starting WebAuthn signing for stored challenge");

        const webauthn = new WebAuthnSigner({
          relyingParty: {
            id: this.domain.domain,
            name: this.domain.company,
          },
        });

        this.attestation = await webauthn.sign(this.challenge);
      }

      // Signature obtained (KMS or WebAuthn) — one step before completion.
      emitCheckoutEvent("checkout_payment_signed", {
        asset: (this.tokensymbolValue || "").trim(),
        network: (this.networkNameValue || "").trim(),
        amount_crypto: (this.wmAmountCryptoValue || "").trim(),
        signing_method: this.keyCredential ? "kms_key_credential" : "dfns_passkey",
      });

      this.showLoadingScreen("Payment in progress...");
      this.proceedToPurchase();
    } catch (error) {
      this.hideLoadingScreen();
      this.releaseButton();
      debugLog("Error during WebAuthn signing:", error);
    }
  }

  async proceedToPurchase() {
    const data = this.typedData;

    data.session = this.sessionValue;
    data.address = this.addressValue;
    data.challengeIdentifier = this.challenge.challengeIdentifier;
    data.firstFactor = this.attestation;
    data.permitId = this.permitidValue;
    data.tokenSymbol = this.tokensymbolValue;

    debugLog("/payment/ endpoint data:", data);

    try {
      const res = await httpService.post(`/payment/`, data);

      switch (res.type) {
        case "channel":
          channelService.connect(res.data);
          break;
        case "html":
          Turbo.renderStreamMessage(res.data);
          if (res.channel) channelService.connect(res.channel);
          break;
        case "redirect":
          Turbo.visit(res.data);
          break;
      }
    } catch (error) {
      this.hideLoadingScreen();
      this.releaseButton();
      debugLog("error", error.message);
    }
  }

  // An automatic finalize that did not get through leaves the button as the way to retry.
  releaseButton() {
    this.submitButtonTarget.disabled = false;
    // Auto-finalize screens render a processing state with the button hidden so the
    // finalize screen never flashes. When the automatic attempt fails, swap that
    // processing state for the retry prompt and reveal the button.
    if (this.hasProcessingTarget) this.processingTarget.classList.add("hidden");
    if (this.hasRetryTarget) this.retryTarget.classList.remove("hidden");
    this.submitButtonTarget.classList.remove("hidden");
  }

  showLoadingScreen(message) {
    this.dispatch("showLoadingScreen", { detail: { text: message } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
